import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Plus, Paperclip, CheckCircle2, AlertTriangle, XCircle, Pencil, Trash2 } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { PlanificacionForm, ESTADOS_PLAN } from "@/components/PlanificacionForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function CoordPlanificaciones() {
  const { config } = useAuth();
  const location = useLocation();
  const [planes, setPlanes] = useState(null);
  const [docentes, setDocentes] = useState([]);
  const [materias, setMaterias] = useState([]);
  const [filtroDocente, setFiltroDocente] = useState(location.state?.docenteId || "");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [form, setForm] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const [revision, setRevision] = useState({ comentario: "", puntuacion: "" });

  const cargar = () => {
    api.get("/planificaciones").then((r) => setPlanes(r.data)).catch(() => setPlanes([]));
  };

  useEffect(() => {
    cargar();
    api.get("/usuarios", { params: { rol: "docente" } }).then((r) => setDocentes(r.data)).catch(() => {});
    api.get("/materias").then((r) => setMaterias(r.data)).catch(() => {});
  }, []);

  const guardar = async (datos) => {
    try {
      if (datos.id) await api.put(`/planificaciones/${datos.id}`, datos);
      else await api.post("/planificaciones", datos);
      toast.success(datos.id ? "Planificación actualizada" : "Planificación registrada");
      setForm(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const revisar = async (estado) => {
    if (estado !== "aprobada" && !revision.comentario.trim()) {
      return toast.error("Escriba el comentario para el docente");
    }
    try {
      await api.post(`/planificaciones/${detalle.id}/revisar`, {
        estado,
        comentario: revision.comentario || null,
        puntuacion: revision.puntuacion === "" ? null : Number(revision.puntuacion),
      });
      toast.success("Revisión enviada al docente");
      setDetalle(null);
      setRevision({ comentario: "", puntuacion: "" });
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const eliminar = async (p) => {
    try {
      await api.delete(`/planificaciones/${p.id}`);
      toast.success("Planificación eliminada");
      setDetalle(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!planes) return <Loading />;

  const visibles = planes.filter((p) => (!filtroDocente || p.docente_id === filtroDocente)
    && (!filtroEstado || p.estado === filtroEstado));

  return (
    <div data-testid="coord-planificaciones">
      <PageTitle title="Planificaciones docentes"
                 subtitle="Revise, evalúe y devuelva observaciones sobre la planificación de cada docente.">
        <Button className="rounded-full" data-testid="nueva-planificacion"
                onClick={() => setForm({ docente_id: filtroDocente || "" })}>
          <Plus className="mr-2 h-4 w-4" /> Nueva planificación
        </Button>
      </PageTitle>

      <div className="mb-6 flex flex-wrap gap-3">
        <select value={filtroDocente} onChange={(e) => setFiltroDocente(e.target.value)}
                data-testid="filtro-docente"
                className="rounded-full border border-border bg-card px-4 py-2 text-sm">
          <option value="">Todos los docentes</option>
          {docentes.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
        </select>
        <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}
                data-testid="filtro-estado"
                className="rounded-full border border-border bg-card px-4 py-2 text-sm">
          <option value="">Todos los estados</option>
          {Object.entries(ESTADOS_PLAN).map(([v, e]) => <option key={v} value={v}>{e.label}</option>)}
        </select>
        <span className="self-center text-xs text-muted-foreground" data-testid="contador-planes">
          {visibles.length} planificación(es)
        </span>
      </div>

      {visibles.length === 0 ? (
        <Empty testId="planes-vacios">No hay planificaciones que coincidan con el filtro.</Empty>
      ) : (
        <div className="space-y-3" data-testid="lista-planificaciones">
          {visibles.map((p) => (
            <article key={p.id} className="lift rounded-lg border border-border/60 bg-card p-5">
              <button className="w-full text-left" data-testid={`plan-${p.id}`}
                      onClick={() => { setDetalle(p); setRevision({ comentario: p.comentario_revision || "", puntuacion: p.puntuacion ?? "" }); }}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-display text-base font-bold">{p.titulo}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.docente_nombre} · {p.materia_nombre} · {p.grado} · {p.periodo}
                      {p.semana ? ` · ${p.semana}` : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {p.archivo_nombre && <Paperclip className="h-3 w-3 text-muted-foreground" />}
                    {p.puntuacion != null && (
                      <Badge variant="outline" className="font-mono text-[10px]">{p.puntuacion}/100</Badge>
                    )}
                    <Badge variant={ESTADOS_PLAN[p.estado]?.variant}>{ESTADOS_PLAN[p.estado]?.label}</Badge>
                  </div>
                </div>
              </button>
              <div className="mt-4 flex gap-2">
                <Button size="sm" variant="outline" className="rounded-full" data-testid={`editar-plan-${p.id}`}
                        onClick={() => setForm(p)}>
                  <Pencil className="mr-1 h-3 w-3" /> Editar
                </Button>
                <Button size="sm" variant="ghost" data-testid={`eliminar-plan-${p.id}`} onClick={() => eliminar(p)}>
                  <Trash2 className="h-3 w-3 text-destructive" />
                </Button>
              </div>
            </article>
          ))}
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="modal-plan-form">
          <DialogHeader>
            <DialogTitle className="font-display">
              {form?.id ? "Editar planificación" : "Nueva planificación"}
            </DialogTitle>
            <DialogDescription>El docente recibe una notificación con el registro.</DialogDescription>
          </DialogHeader>
          <PlanificacionForm coordinacion materias={materias} docentes={docentes}
                             periodos={config.periodos} valor={form}
                             onGuardar={(datos) => guardar({ ...datos, id: form?.id })} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!detalle} onOpenChange={(o) => !o && setDetalle(null)}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto" data-testid="modal-plan-detalle">
          {detalle && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display">{detalle.titulo}</DialogTitle>
                <DialogDescription>
                  {detalle.docente_nombre} · {detalle.materia_nombre} · {detalle.grado} · {detalle.periodo}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                {[["Objetivos", detalle.objetivos], ["Contenidos", detalle.contenidos],
                  ["Actividades y estrategias", detalle.actividades], ["Recursos y evaluación", detalle.recursos]]
                  .filter(([, v]) => v).map(([t, v]) => (
                    <div key={t}>
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">{t}</p>
                      <p className="mt-1 whitespace-pre-wrap">{v}</p>
                    </div>
                  ))}
                {detalle.archivo_nombre && (
                  <a href={`${API}/planificaciones/${detalle.id}/archivo`} target="_blank" rel="noreferrer"
                     data-testid="ver-archivo-plan"
                     className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                    <Paperclip className="h-3 w-3" /> {detalle.archivo_nombre}
                  </a>
                )}
              </div>

              <div className="mt-6 space-y-4 rounded-lg border border-border bg-muted/30 p-5">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
                  Evaluación de coordinación
                </p>
                <div className="space-y-2">
                  <Label>Comentario para el docente</Label>
                  <Textarea rows={3} value={revision.comentario} data-testid="revision-comentario"
                            onChange={(e) => setRevision({ ...revision, comentario: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Puntuación (0-100, opcional)</Label>
                  <Input type="number" min="0" max="100" className="w-32" value={revision.puntuacion}
                         data-testid="revision-puntuacion"
                         onChange={(e) => setRevision({ ...revision, puntuacion: e.target.value })} />
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button className="rounded-full" onClick={() => revisar("aprobada")} data-testid="aprobar-plan">
                    <CheckCircle2 className="mr-2 h-4 w-4" /> Aprobar
                  </Button>
                  <Button variant="outline" className="rounded-full" onClick={() => revisar("correcciones")}
                          data-testid="corregir-plan">
                    <AlertTriangle className="mr-2 h-4 w-4" /> Pedir correcciones
                  </Button>
                  <Button variant="outline" className="rounded-full" onClick={() => revisar("rechazada")}
                          data-testid="rechazar-plan">
                    <XCircle className="mr-2 h-4 w-4 text-destructive" /> Rechazar
                  </Button>
                </div>
                {detalle.revisado_por && (
                  <p className="text-xs text-muted-foreground">
                    Última revisión: {detalle.revisado_por} · {String(detalle.revisado_en).slice(0, 10)}
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
